import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill-new";
import api from "../../../api/api";
import toast from "react-hot-toast";
import "react-quill-new/dist/quill.snow.css";

const emptySeo = {
  meta_title: "",
  meta_description: "",
  focus_keyword: "",
  keywords: [],
  canonical_url: "",
  og_title: "",
  og_description: "",
  og_image: "",
  no_index: false,
  no_follow: false,
};

const PostForm = ({ initialData, onSubmit, loading }) => {
  const [categories, setCategories] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);

  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    content: "",
    status: "draft",
    featured_image: "",
    category_ids: [],
    tags: "",
    allow_comments: true,
    seo: emptySeo,
  });

  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, 4, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["blockquote", "code-block"],
        ["link", "image", "video"],
        ["clean"],
      ],
    }),
    []
  );

  useEffect(() => {
    if (initialData) {
      setForm((prev) => ({
        ...prev,
        ...initialData,
        tags: Array.isArray(initialData.tags)
          ? initialData.tags.join(", ")
          : initialData.tags || "",
        category_ids: initialData.category_ids || [],
        seo: {
          ...emptySeo,
          ...(initialData.seo || {}),
          keywords: initialData.seo?.keywords || [],
        },
      }));
    }
  }, [initialData]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await api.get("/categories");
        setCategories(res.data || []);
      } catch (err) {
        toast.error(err.cleanMessage || "Failed to load categories");
      }
    };

    loadCategories();
  }, []);

  const updateForm = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateSeo = (key, value) => {
    setForm((prev) => ({
      ...prev,
      seo: {
        ...prev.seo,
        [key]: value,
      },
    }));
  };

  const uploadFeatured = async (file) => {
    if (!file) return;

    setMediaLoading(true);

    try {
      const body = new FormData();
      body.append("file", file);

      const res = await api.post("/media/upload", body, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      updateForm("featured_image", res.data.url);
      toast.success("Featured image uploaded");
    } catch (err) {
      toast.error(err.cleanMessage || "Image upload failed");
    } finally {
      setMediaLoading(false);
    }
  };

  const analyzeSeo = async () => {
    try {
      const res = await api.post("/seo/analyze", {
        title: form.title,
        excerpt: form.excerpt,
        content: form.content,
        ...form.seo,
      });

      if (res.data.status === "good") {
        toast.success(`SEO Score: ${res.data.score}`);
      } else {
        toast.error(`SEO Score: ${res.data.score}. Check warnings.`);
      }

      console.log(res.data.warnings);
    } catch (err) {
      toast.error(err.cleanMessage || "SEO analysis failed");
    }
  };

  const submit = (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      toast.error("Post title is required");
      return;
    }

    if (!form.content.trim()) {
      toast.error("Post body is required");
      return;
    }

    onSubmit({
      ...form,
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      tags: form.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean),
      seo: {
        ...form.seo,
        keywords: form.seo.keywords,
      },
    });
  };

  return (
    <form onSubmit={submit} className="grid xl:grid-cols-[1fr_360px] gap-6">
      <div className="space-y-6">
        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <label className="block mb-5">
            <span className="font-bold text-slate-700">Post Title</span>
            <input
              value={form.title}
              onChange={(e) => updateForm("title", e.target.value)}
              required
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
              placeholder="Enter post title"
            />
          </label>

          <label className="block mb-5">
            <span className="font-bold text-slate-700">Excerpt</span>
            <textarea
              value={form.excerpt}
              onChange={(e) => updateForm("excerpt", e.target.value)}
              rows="3"
              className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
              placeholder="Short post summary"
            />
          </label>

          <label className="block">
            <span className="font-bold text-slate-700">Post Body</span>

            <div className="mt-2 bg-white rounded-2xl overflow-hidden border border-slate-200">
              <ReactQuill
                theme="snow"
                value={form.content}
                onChange={(value) => updateForm("content", value)}
                modules={quillModules}
                placeholder="Write your post content here..."
              />
            </div>
          </label>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-xl font-black text-[#0b6b1d] mb-4">
            SEO Settings
          </h3>

          <div className="grid md:grid-cols-2 gap-4">
            <label>
              <span className="font-bold text-sm">Meta Title</span>
              <input
                value={form.seo.meta_title}
                maxLength={60}
                onChange={(e) => updateSeo("meta_title", e.target.value)}
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="SEO title"
              />
              <small className="text-slate-500">
                {form.seo.meta_title.length}/60
              </small>
            </label>

            <label>
              <span className="font-bold text-sm">Focus Keyword</span>
              <input
                value={form.seo.focus_keyword}
                onChange={(e) => updateSeo("focus_keyword", e.target.value)}
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="Main keyword"
              />
            </label>

            <label className="md:col-span-2">
              <span className="font-bold text-sm">Meta Description</span>
              <textarea
                value={form.seo.meta_description}
                maxLength={160}
                onChange={(e) => updateSeo("meta_description", e.target.value)}
                rows="3"
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="SEO description"
              />
              <small className="text-slate-500">
                {form.seo.meta_description.length}/160
              </small>
            </label>

            <label>
              <span className="font-bold text-sm">Keywords</span>
              <input
                value={form.seo.keywords.join(", ")}
                onChange={(e) =>
                  updateSeo(
                    "keywords",
                    e.target.value
                      .split(",")
                      .map((keyword) => keyword.trim())
                      .filter(Boolean)
                  )
                }
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="education, school, pupils"
              />
            </label>

            <label>
              <span className="font-bold text-sm">Canonical URL</span>
              <input
                value={form.seo.canonical_url}
                onChange={(e) => updateSeo("canonical_url", e.target.value)}
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="https://example.com/post-url"
              />
            </label>

            <label>
              <span className="font-bold text-sm">OG Title</span>
              <input
                value={form.seo.og_title}
                onChange={(e) => updateSeo("og_title", e.target.value)}
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="Social media title"
              />
            </label>

            <label>
              <span className="font-bold text-sm">OG Image URL</span>
              <input
                value={form.seo.og_image}
                onChange={(e) => updateSeo("og_image", e.target.value)}
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="https://example.com/image.jpg"
              />
            </label>

            <label className="md:col-span-2">
              <span className="font-bold text-sm">OG Description</span>
              <textarea
                value={form.seo.og_description}
                onChange={(e) => updateSeo("og_description", e.target.value)}
                rows="3"
                className="mt-2 w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
                placeholder="Social media description"
              />
            </label>

            <div className="flex items-center gap-6 md:col-span-2">
              <label className="flex items-center gap-2 font-bold text-sm">
                <input
                  type="checkbox"
                  checked={form.seo.no_index}
                  onChange={(e) => updateSeo("no_index", e.target.checked)}
                />
                No Index
              </label>

              <label className="flex items-center gap-2 font-bold text-sm">
                <input
                  type="checkbox"
                  checked={form.seo.no_follow}
                  onChange={(e) => updateSeo("no_follow", e.target.checked)}
                />
                No Follow
              </label>
            </div>
          </div>

          <button
            type="button"
            onClick={analyzeSeo}
            className="mt-5 px-5 py-3 rounded-2xl bg-slate-900 hover:bg-[#0b6b1d] text-white font-bold transition"
          >
            Analyze SEO
          </button>
        </div>
      </div>

      <aside className="space-y-6">
        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-black text-[#0b6b1d] mb-4">Publish</h3>

          <select
            value={form.status}
            onChange={(e) => updateForm("status", e.target.value)}
            className="w-full border border-slate-200 rounded-2xl px-4 py-3 mb-4 outline-none focus:border-[#0b6b1d]"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="scheduled">Scheduled</option>
            <option value="archived">Archived</option>
          </select>

          <label className="flex items-center gap-2 mb-5 font-bold text-sm">
            <input
              type="checkbox"
              checked={form.allow_comments}
              onChange={(e) => updateForm("allow_comments", e.target.checked)}
            />
            Allow Comments
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-2xl bg-[#0b6b1d] hover:bg-[#ff6600] text-white font-black transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Saving..." : "Save Post"}
          </button>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-black text-[#0b6b1d] mb-4">Featured Image</h3>

          {form.featured_image ? (
            <img
              src={form.featured_image}
              alt="Featured"
              className="w-full h-44 object-cover rounded-2xl mb-4 border"
            />
          ) : (
            <div className="w-full h-44 rounded-2xl mb-4 border border-dashed flex items-center justify-center text-slate-400 text-sm">
              No featured image
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            onChange={(e) => uploadFeatured(e.target.files?.[0])}
            className="w-full text-sm"
          />

          {mediaLoading && (
            <p className="text-sm mt-2 text-slate-500">Uploading...</p>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-black text-[#0b6b1d] mb-4">Categories</h3>

          {categories.length > 0 ? (
            <div className="space-y-2">
              {categories.map((cat) => (
                <label
                  key={cat.id}
                  className="flex items-center gap-2 text-sm font-semibold text-slate-700"
                >
                  <input
                    type="checkbox"
                    checked={form.category_ids.includes(cat.id)}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      setForm((prev) => ({
                        ...prev,
                        category_ids: checked
                          ? [...prev.category_ids, cat.id]
                          : prev.category_ids.filter((id) => id !== cat.id),
                      }));
                    }}
                  />
                  {cat.name}
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No categories found.</p>
          )}
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-black text-[#0b6b1d] mb-4">Tags</h3>

          <input
            value={form.tags}
            onChange={(e) => updateForm("tags", e.target.value)}
            className="w-full border border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-[#0b6b1d]"
            placeholder="education, school, training"
          />

          <p className="text-xs text-slate-500 mt-2">
            Separate tags with commas.
          </p>
        </div>
      </aside>
    </form>
  );
};

export default PostForm;